import { PANEL_SIZE_MAX_MM, PANEL_SIZE_MIN_MM } from '../types';

/**
 * The single source of truth for numeric limits.
 *
 * The UI builds its sliders from these and the validator clamps against the
 * same numbers, so a design loaded from a file can never sit outside what the
 * controls allow.
 */
export interface Range {
  min: number;
  max: number;
  step: number;
  unit?: string;
}

export const CONSTRAINTS = {
  panelSize: { min: PANEL_SIZE_MIN_MM, max: PANEL_SIZE_MAX_MM, step: 1, unit: 'mm' },
  gap: { min: 0, max: 20, step: 0.1, unit: 'mm' },
  baseThickness: { min: 0.8, max: 12, step: 0.1, unit: 'mm' },
  reliefDepth: { min: 0.2, max: 40, step: 0.1, unit: 'mm' },
  edgeWidth: { min: 0, max: 25, step: 0.5, unit: 'mm' },
  segments: { min: 16, max: 512, step: 8 },
  columns: { min: 1, max: 12, step: 1 },
  rows: { min: 1, max: 12, step: 1 },
  gamma: { min: 0.25, max: 4, step: 0.05 },
  contrast: { min: 0, max: 1, step: 0.01 },
  terraceSteps: { min: 0, max: 24, step: 1 },
  stripWidth: { min: 3, max: 30, step: 0.5, unit: 'mm' },
  /**
   * The air gap is the single most important LED number: it decides whether
   * individual emitters blend. Its floor is set well above what a groove in a
   * panel could ever offer, because that is the point of the light box.
   */
  channelDepth: { min: 0.6, max: 8, step: 0.1, unit: 'mm' },
  airGap: { min: 6, max: 60, step: 1, unit: 'mm' },
  frameWall: { min: 1.2, max: 6, step: 0.2, unit: 'mm' },
  frameFloor: { min: 0.8, max: 5, step: 0.2, unit: 'mm' },
  ledInset: { min: 2, max: 60, step: 1, unit: 'mm' },
} as const satisfies Record<string, Range>;

/**
 * Common LED strip widths, in millimetres.
 *
 * Channel width is the one dimension that has to match a physical part, so the
 * UI offers the usual strip sizes rather than leaving it to a bare slider.
 */
export const LED_STRIP_PRESETS = [
  { width: 5, label: '5 mm — narrow COB' },
  { width: 8, label: '8 mm — 2835 / COB' },
  { width: 10, label: '10 mm — 5050 single row' },
  { width: 12, label: '12 mm — 5050 with wiring' },
  { width: 15, label: '15 mm — high density' },
] as const;

/**
 * Mesh resolution presets.
 *
 * Segment counts are powers of two so a preview at a lower quality lands on a
 * subset of the export grid's sample positions, which keeps the preview honest
 * about where the peaks are.
 */
export const QUALITY_PRESETS = [
  { id: 'draft', label: 'Draft', segments: 48, help: 'Fast, for exploring shapes.' },
  { id: 'normal', label: 'Normal', segments: 96, help: 'Good balance for most designs.' },
  { id: 'fine', label: 'Fine', segments: 192, help: 'Resolves small detail; slower.' },
  { id: 'ultra', label: 'Ultra', segments: 384, help: 'Print-ready for fine noise textures.' },
] as const;

export type QualityId = (typeof QUALITY_PRESETS)[number]['id'];

export function segmentsForQuality(id: QualityId): number {
  return QUALITY_PRESETS.find((preset) => preset.id === id)?.segments ?? 96;
}

/**
 * Suggested mesh resolution for a panel size.
 *
 * Detail is only as good as the sample spacing, so the useful segment count
 * scales with the panel: 250 mm at 96 segments is a 2.6 mm sample spacing,
 * which visibly staircases a fine pattern.
 */
export function suggestedSegments(panelSizeMm: number, targetSampleMm = 0.8): number {
  const raw = panelSizeMm / targetSampleMm;
  const clamped = Math.min(CONSTRAINTS.segments.max, Math.max(CONSTRAINTS.segments.min, raw));
  return Math.round(clamped / 8) * 8;
}
